import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, Info, MessageCircle, Phone, StickyNote } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Area, Card, Confirm, DeleteBtn, IconBtn, Input, OBtn, PageTitle, Select, Skel, YBtn, useSaver } from "@/admin/ui";
import { adminApi, downloadBlob, fmtDate, useAdmin, useRefresh } from "@/admin/lib";
import { errorMessage, waLink } from "@/lib/api";

const STATUSES = ["new", "contacted", "converted", "closed"];

const NotesDialog = ({ lead, onClose, onSaved }) => {
  const [notes, setNotes] = useState(lead?.notes || "");
  const [saving, run] = useSaver();
  if (!lead) return null;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-2xl bg-white" data-testid="lead-notes-dialog">
        <DialogHeader><DialogTitle className="font-heading text-navy">Lead: {lead.name}</DialogTitle></DialogHeader>
        <div className="space-y-1 text-sm text-body">
          <p><b>Phone:</b> {lead.phone} · <b>Email:</b> {lead.email || "-"}</p>
          <p><b>Interest:</b> {lead.product || lead.service || "-"} · <b>Source:</b> {lead.source}</p>
          {lead.message && <p className="rounded-xl bg-page p-3">{lead.message}</p>}
        </div>
        <Area rows={5} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Add follow-up notes" testId="lead-notes-input" />
        <YBtn loading={saving} onClick={async () => { if (await run(() => adminApi.put(`/admin/records/leads/${lead.id}`, { notes }), "Notes saved")) { onSaved(); onClose(); } }} testId="lead-notes-save-btn">Save notes</YBtn>
      </DialogContent>
    </Dialog>
  );
};

export default function Leads() {
  const [f, setF] = useState({ q: "", status: "", source: "", product: "", date_from: "", date_to: "" });
  const [page, setPage] = useState(1);
  const [sel, setSel] = useState([]);
  const [noteLead, setNoteLead] = useState(null);
  const refresh = useRefresh();
  const qs = new URLSearchParams(Object.fromEntries(Object.entries({ ...f, page, limit: 25 }).filter(([, v]) => v))).toString();
  const { data, isLoading } = useAdmin(`/admin/records/leads?${qs}`);
  const { data: settings } = useAdmin("/admin/settings");
  const items = data?.items || [];
  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); setPage(1); };
  const reload = () => refresh("/admin/records/leads");

  const updateStatus = async (id, status) => {
    try { await adminApi.put(`/admin/records/leads/${id}`, { status }); toast.success("Status updated"); reload(); } catch (e) { toast.error(errorMessage(e)); }
  };
  const bulk = async (action, status) => {
    try {
      const { data: r } = await adminApi.post("/admin/leads/bulk", { ids: sel, action, status });
      toast.success(`${r.affected} lead(s) updated`);
      setSel([]);
      reload();
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const del = async (id) => { await adminApi.delete(`/admin/records/leads/${id}`); toast.success("Lead deleted"); reload(); };

  return (
    <>
      <PageTitle title="Leads & Inquiries" subtitle={`${data?.total ?? 0} total leads`}>
        <OBtn onClick={() => downloadBlob("/admin/export/leads", "leads.csv", Object.fromEntries(Object.entries(f).filter(([, v]) => v)))} testId="leads-export-btn"><Download className="h-4 w-4" />Export CSV</OBtn>
      </PageTitle>
      <div className="mb-5 flex items-center gap-3 rounded-2xl border border-[#FDE68A] bg-[#FEF3C7] px-4 py-3 text-sm text-[#92400E]" data-testid="leads-retention-notice">
        <Info className="h-4 w-4 shrink-0" />Leads auto-delete after <b>{settings?.retention_days ?? 30} days</b> (orders are never deleted).
        <Link to="/admin/settings" className="ml-auto font-bold underline">Change retention</Link>
      </div>
      <Card>
        <div className="mb-4 grid gap-3 md:grid-cols-3 xl:grid-cols-6">
          <Input placeholder="Search name, phone, email" value={f.q} onChange={set("q")} testId="leads-search-input" className="xl:col-span-2" />
          <Select value={f.status} onChange={set("status")} options={[{ value: "", label: "All statuses" }, ...STATUSES]} testId="leads-status-filter" />
          <Input placeholder="Source (e.g. contact page)" value={f.source} onChange={set("source")} testId="leads-source-filter" />
          <Input placeholder="Product / service" value={f.product} onChange={set("product")} testId="leads-product-filter" />
          <div className="flex gap-2"><Input type="date" value={f.date_from} onChange={set("date_from")} testId="leads-date-from" /><Input type="date" value={f.date_to} onChange={set("date_to")} testId="leads-date-to" /></div>
        </div>
        {sel.length > 0 && (
          <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl bg-page p-3" data-testid="leads-bulk-bar">
            <span className="text-sm font-bold text-navy">{sel.length} selected</span>
            {STATUSES.map((s) => <OBtn key={s} className="px-3 py-1.5 text-xs capitalize" onClick={() => bulk("status", s)} testId={`bulk-status-${s}`}>Mark {s}</OBtn>)}
            <Confirm title={`Delete ${sel.length} leads?`} onConfirm={() => bulk("delete")}><button className="rounded-full bg-red-600 px-3 py-1.5 text-xs font-bold text-white" data-testid="bulk-delete-btn">Delete</button></Confirm>
          </div>
        )}
        {isLoading ? <Skel /> : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-sm" data-testid="leads-table">
              <thead><tr className="border-b border-line text-left text-xs uppercase tracking-wider text-muted2">
                <th className="w-8 py-2"><Checkbox checked={items.length > 0 && sel.length === items.length} onCheckedChange={(v) => setSel(v ? items.map((i) => i.id) : [])} data-testid="leads-select-all" /></th>
                {["Name", "Contact", "Interest", "Source", "Status", "Date", ""].map((h) => <th key={h} className="py-2 pr-3 font-bold">{h}</th>)}
              </tr></thead>
              <tbody>
                {items.map((l) => (
                  <tr key={l.id} className="border-b border-line/60 align-top last:border-0" data-testid={`lead-row-${l.id}`}>
                    <td className="py-3"><Checkbox checked={sel.includes(l.id)} onCheckedChange={(v) => setSel(v ? [...sel, l.id] : sel.filter((x) => x !== l.id))} /></td>
                    <td className="py-3 pr-3"><p className="font-semibold text-navy">{l.name}</p>{l.notes && <p className="mt-0.5 line-clamp-1 text-xs text-muted2">{l.notes}</p>}</td>
                    <td className="py-3 pr-3 text-body"><p>{l.phone}</p><p className="text-xs">{l.email}</p></td>
                    <td className="max-w-[200px] py-3 pr-3 text-body">{l.product || l.service || "-"}</td>
                    <td className="py-3 pr-3 text-body">{l.source}</td>
                    <td className="py-3 pr-3"><select value={l.status} onChange={(e) => updateStatus(l.id, e.target.value)} className="rounded-full border border-line bg-white px-2.5 py-1 text-xs font-bold capitalize text-navy" data-testid={`lead-status-${l.id}`}>{STATUSES.map((s) => <option key={s}>{s}</option>)}</select></td>
                    <td className="whitespace-nowrap py-3 pr-3 text-xs text-muted2">{fmtDate(l.created_at)}</td>
                    <td className="py-3"><div className="flex">
                      <a href={`tel:${l.phone}`} className="grid h-8 w-8 place-items-center rounded-lg text-ocean hover:bg-page" aria-label="Call" data-testid={`lead-call-${l.id}`}><Phone className="h-4 w-4" /></a>
                      <a href={waLink(l.phone, `Hi ${l.name}, thanks for contacting Own Brand Technologies.`)} target="_blank" rel="noopener noreferrer" className="grid h-8 w-8 place-items-center rounded-lg text-success hover:bg-page" aria-label="WhatsApp" data-testid={`lead-whatsapp-${l.id}`}><MessageCircle className="h-4 w-4" /></a>
                      <IconBtn label="Notes" onClick={() => setNoteLead(l)} testId={`lead-notes-${l.id}`}><StickyNote className="h-4 w-4" /></IconBtn>
                      <DeleteBtn onConfirm={() => del(l.id)} testId={`lead-delete-${l.id}`} title="Delete this lead?" />
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!items.length && <p className="py-10 text-center text-sm text-muted2">No leads found.</p>}
          </div>
        )}
        <div className="mt-4 flex items-center justify-end gap-2 text-sm">
          <OBtn className="px-4 py-1.5" disabled={page <= 1} onClick={() => setPage(page - 1)} testId="leads-prev-page">Prev</OBtn>
          <span className="text-muted2">Page {page} of {Math.max(1, Math.ceil((data?.total || 0) / 25))}</span>
          <OBtn className="px-4 py-1.5" disabled={page * 25 >= (data?.total || 0)} onClick={() => setPage(page + 1)} testId="leads-next-page">Next</OBtn>
        </div>
      </Card>
      {noteLead && <NotesDialog lead={noteLead} onClose={() => setNoteLead(null)} onSaved={reload} />}
    </>
  );
}
